import { Box, ButtonBase } from "@mui/material";
import { useTranslation } from "next-i18next";
import { useGenericActivityFromElysiumMutation } from "src/api/client/generic-activity-from-elysium.api";
import { EnumActivityType } from "src/api/client/generic-activity.api";
import { IconColumnView } from "src/components/icons-mui/column-view";
import { IconGrid } from "src/components/icons-mui/grid";
import { ViewMode, TViewMode } from "src/components/pages/search/store/search-store";

export interface IViewToggleButton {
    viewMode: TViewMode;
    onChange: (viewMode: TViewMode) => void;
    isDisabled?: boolean;
}

export const ViewToggleButton = ({ viewMode, onChange, isDisabled = false }: IViewToggleButton) => {
    const { t } = useTranslation("extended-search");
    const genericActivity = useGenericActivityFromElysiumMutation();

    const toggleViewMode = () => {
        const newViewMode = viewMode === ViewMode.Grid ? ViewMode.Table : ViewMode.Grid;
        void genericActivity({
            activityTypeId: EnumActivityType.SearchViewSwitched,
            parameter: `view=${newViewMode}`,
            referrer: window.location.href,
        });
        onChange(newViewMode);
    };

    const viewButtons = [
        {
            mode: ViewMode.Grid,
            Icon: IconGrid,
            staticLabel: t("labels.view-grid"),
            borderRadius: "8px 0 0 8px",
            borderLeftWidth: "1px",
            inactiveColor: "inherit",
        },
        {
            mode: ViewMode.Table,
            Icon: IconColumnView,
            staticLabel: t("labels.view-list"),
            borderRadius: "0 8px 8px 0",
            borderLeftWidth: "0px",
            inactiveColor: "gray.800",
        },
    ];

    return (
        <Box
            role="group"
            aria-label={t("labels.view-toggle-group")}
            sx={{
                display: "inline-flex",
                flexShrink: 0,
            }}
        >
            {viewButtons.map(({ mode, Icon, staticLabel, borderRadius, borderLeftWidth, inactiveColor }) => {
                const isActive = viewMode === mode;
                return (
                    <ButtonBase
                        key={mode}
                        onClick={toggleViewMode}
                        disabled={isDisabled}
                        sx={{
                            width: "40px",
                            height: "40px",
                            border: "1px solid",
                            borderColor: isDisabled ? "gray.200" : "gray.800",
                            borderLeftWidth,
                            bgcolor: isActive ? "gray.800" : "common.white",
                            color: isActive ? "white" : inactiveColor,
                            borderRadius,
                            transition: "background-color 250ms",
                            "&:hover, &:focus-visible": {
                                bgcolor: isActive ? "gray.600" : "gray.100",
                            },
                        }}
                        aria-label={staticLabel}
                        aria-pressed={isActive}
                    >
                        <Icon sx={{ fontSize: 24 }} />
                    </ButtonBase>
                );
            })}
        </Box>
    );
};
